import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getCurrentCommissionRate } from './commissionSettingsService';

const REFERRAL_COOKIE_NAME = 'jampackd_ref';
const REFERRAL_COOKIE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Validate that a referral code exists and is active
 */
export const validateReferralCodeExists = async (referralCode: string): Promise<{ valid: boolean; affiliateId?: string }> => {
  try {
    const affiliateQuery = query(
      collection(db, 'affiliateApplications'),
      where('referralCode', '==', referralCode),
      where('status', '==', 'approved')
    );
    
    const affiliateSnapshot = await getDocs(affiliateQuery);
    
    if (affiliateSnapshot.empty) {
      console.warn('Invalid or inactive referral code:', referralCode);
      return { valid: false };
    }
    
    const affiliateDoc = affiliateSnapshot.docs[0];
    return { valid: true, affiliateId: affiliateDoc.id };
  } catch (error) {
    console.error('Error validating referral code:', error);
    return { valid: false };
  }
};

/**
 * Set a referral cookie when a user visits with a referral code
 * Implements last-click overwrite - new codes replace existing ones
 */
export const setReferralCookie = (referralCode: string): void => {
  try {
    const existingCookie = getReferralCookie();
    
    if (existingCookie) {
      console.log('Last-click overwrite: Replacing existing referral cookie:', existingCookie, 'with new code:', referralCode);
    } else {
      console.log('Setting new referral cookie:', referralCode);
    }
    
    const expirationDate = new Date(Date.now() + REFERRAL_COOKIE_DURATION);
    const cookieValue = JSON.stringify({
      code: referralCode,
      timestamp: Date.now()
    });
    
    document.cookie = `${REFERRAL_COOKIE_NAME}=${cookieValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
    
    console.log('Referral cookie set successfully:', referralCode);
  } catch (error) {
    console.error('Error setting referral cookie:', error);
  }
};

/**
 * Get the current referral code from cookies
 */
export const getReferralCookie = (): string | null => {
  try {
    const cookieString = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${REFERRAL_COOKIE_NAME}=`));
    
    if (!cookieString) return null;
    
    const cookieValue = cookieString.split('=')[1];
    const referralData = JSON.parse(cookieValue);
    
    // Check if cookie is still valid (within 30 days)
    if (Date.now() - referralData.timestamp > REFERRAL_COOKIE_DURATION) {
      clearReferralCookie();
      return null;
    }
    
    return referralData.code;
  } catch (error) {
    console.error('Error getting referral cookie:', error);
    return null;
  }
};

/**
 * Clear the referral cookie
 */
export const clearReferralCookie = (): void => {
  try {
    document.cookie = `${REFERRAL_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    console.log('Referral cookie cleared');
  } catch (error) {
    console.error('Error clearing referral cookie:', error);
  }
};

/**
 * Track a click when a user visits with a referral code
 */
export const trackReferralClick = async (referralCode: string): Promise<void> => {
  try {
    // Use the centralized validation function
    const validation = await validateReferralCodeExists(referralCode);
    
    if (!validation.valid || !validation.affiliateId) {
      console.warn('Cannot track click for invalid referral code:', referralCode);
      return;
    }
    
    // Get user agent and IP info (limited for privacy)
    const userAgent = navigator.userAgent;
    
    // Create click record
    await addDoc(collection(db, 'clickRecords'), {
      affiliateId: validation.affiliateId,
      referralCode,
      userAgent,
      timestamp: serverTimestamp(),
      ip: null, // IP tracking would need server-side implementation
    });
    
    console.log('Referral click tracked for affiliate:', validation.affiliateId);
  } catch (error) {
    console.error('Error tracking referral click:', error);
  }
};

/**
 * Track a conversion when a user makes a purchase
 */
export const trackReferralConversion = async (orderId: string, orderValue: number): Promise<void> => {
  try {
    const referralCode = getReferralCookie();
    
    if (!referralCode) {
      console.log('No referral cookie found for conversion tracking');
      return;
    }
    
    // Use the centralized validation function
    const validation = await validateReferralCodeExists(referralCode);
    
    if (!validation.valid || !validation.affiliateId) {
      console.warn('Cannot track conversion for invalid referral code:', referralCode);
      return;
    }
    
    // Create conversion record
    await addDoc(collection(db, 'conversionRecords'), {
      affiliateId: validation.affiliateId,
      orderId,
      orderValue,
      timestamp: serverTimestamp(),
      cookieData: referralCode
    });
    
    // Commission record will be created when payment succeeds
    // Store referral info for later commission creation
    
    console.log('Referral conversion tracked:', {
      affiliateId: validation.affiliateId,
      orderId,
      orderValue,
    });
    
    // Keep the referral cookie for the full 30-day period to allow
    // affiliates to earn from repeat purchases within that window
    // The cookie will automatically expire after 30 days
  } catch (error) {
    console.error('Error tracking referral conversion:', error);
  }
};

/**
 * Handle a referral code with validation and last-click overwrite
 * Can be used by other parts of the application (like from forms, manual entry, etc.)
 */
export const handleReferralCode = async (referralCode: string): Promise<{ success: boolean; message: string }> => {
  try {
    console.log('Handling referral code:', referralCode);
    
    // Validate that the referral code exists and is active
    const validation = await validateReferralCodeExists(referralCode);
    
    if (validation.valid) {
      console.log('✅ Referral code validated successfully:', referralCode);
      
      // Check for existing cookie to log last-click overwrite behavior
      const existingCookie = getReferralCookie();
      if (existingCookie && existingCookie !== referralCode) {
        console.log('🔄 Last-click overwrite: Replacing', existingCookie, 'with', referralCode);
      }
      
      // Set the referral cookie (this will overwrite any existing cookie)
      setReferralCookie(referralCode);
      
      // Track the click
      await trackReferralClick(referralCode);
      
      console.log('✅ Referral tracking completed for:', referralCode);
      return { success: true, message: 'Referral code applied successfully' };
    } else {
      console.warn('❌ Invalid referral code:', referralCode);
      return { success: false, message: 'Invalid or inactive referral code' };
    }
  } catch (error) {
    console.error('Error handling referral code:', error);
    return { success: false, message: 'Error processing referral code' };
  }
};

/**
 * Process URL parameters to check for referral codes
 * Now includes validation BEFORE setting cookie and proper last-click overwrite
 */
export const processReferralFromURL = async (): Promise<void> => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    
    if (referralCode) {
      console.log('Processing referral code from URL:', referralCode);
      
      // Use the centralized handleReferralCode function
      const result = await handleReferralCode(referralCode);
      
      if (!result.success) {
        console.warn('❌ Failed to process referral code from URL:', result.message);
      }
      
      // Clean up the URL by removing the ref parameter regardless of validation result
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('ref');
      
      // Update the URL without the ref parameter
      window.history.replaceState(null, '', newUrl.toString());
    }
  } catch (error) {
    console.error('Error processing referral from URL:', error);
  }
};

/**
 * Get commission rate from admin settings
 */
export const getCommissionRate = async (): Promise<number> => {
  try {
    return await getCurrentCommissionRate();
  } catch (error) {
    console.error('Error getting commission rate:', error);
    return 10; // Default fallback
  }
};